import {
  CalculsFormListeProjection,
  CalculsGlobal,
} from '../../../constants/calculs';
import {
  getDefaultListeProjectionBareme,
  getDefaultPrejudiceMaladieMontant,
  getONIAMRIIMontantDFP,
} from '../../../helpers/bareme/bareme';
import {
  getCapitalisationCoefficient,
  getProrataTemporisBaremes,
} from '../../../helpers/prejudices/capitalisation';
import { Bareme, BaremeCapitalisationValue } from '../../../types/bareme.type';
import {
  ListeProjectionAgrement,
  ListeProjectionAgrementRow,
  ListeProjectionFonctionnelPermanent,
  ListeProjectionFonctionnelPermanentRow,
  ListeProjectionIncidenceProfessionelle,
  ListeProjectionIncidenceProfessionelleRow,
  ListeProjectionMaladie,
  ListeProjectionMaladieRow,
  PGPFReliquatAndPGPFReliquatActivationStatus,
  PrejudiceFormListeProjection,
  PrejudiceType,
} from '../../../types/prejudice.type';
import { Procedure } from '../../../types/procedure.type';
import { Victime } from '../../../types/victime.type';
import {
  BaremeDefaultMontant,
  prejudiceBaseDefaultValues,
} from '../defaultValues';

export type MaladieExistingValues = Omit<
  Partial<ListeProjectionMaladie>,
  'rows'
> & {
  rows?: Partial<ListeProjectionMaladieRow>[];
};
export type DfpExistingValues = Omit<
  Partial<ListeProjectionFonctionnelPermanent>,
  'rows'
> & {
  rows?: Partial<ListeProjectionFonctionnelPermanentRow>[];
};
export type IncidenceProfessionnelleExistingValues = Omit<
  Partial<ListeProjectionIncidenceProfessionelle>,
  'rows'
> & {
  rows?: Partial<ListeProjectionIncidenceProfessionelleRow>[];
};
export type AgrementExistingValues = Omit<
  Partial<ListeProjectionAgrement>,
  'rows'
> & {
  rows?: Partial<ListeProjectionAgrementRow>[];
};
export type ListeProjectionExistingValues = Omit<
  Partial<PrejudiceFormListeProjection>,
  'prejudiceValues'
> & {
  prejudiceValues?:
    | MaladieExistingValues
    | DfpExistingValues
    | IncidenceProfessionnelleExistingValues
    | AgrementExistingValues;
};

const getPrejudiceValues = (parameters: {
  prejudiceType: PrejudiceType;
  prejudiceValues: ListeProjectionExistingValues['prejudiceValues'] | undefined;
  procedure: Procedure;
  baremes: Bareme[];
  victime?: Partial<Victime>;
  dateConsolidation: Date | undefined;
  baremeRIIRoundAge: string | undefined;
  baremeRIIRoundTauxDFP: string | undefined;
  baremeIndemnisation: string | undefined;
  baremeValueChoice: string | undefined;
}) => {
  const {
    prejudiceType,
    prejudiceValues,
    procedure,
    baremes,
    victime,
    dateConsolidation,
    baremeRIIRoundAge,
    baremeRIIRoundTauxDFP,
    baremeIndemnisation,
    baremeValueChoice,
  } = parameters;
  switch (prejudiceType) {
    case 'PREJUDICE_AGREMENT':
    case 'AUTRES_POSTES_DE_PREJUDICES':
      return getInitialValuesPrejudiceAgrement(
        prejudiceValues as ListeProjectionAgrement | undefined,
      );
    case 'DEFICIT_FONCTIONNEL_PERMANENT':
      return getInitialValuesDeficitFonctionnelPermanent({
        values: prejudiceValues as
          | ListeProjectionFonctionnelPermanent
          | undefined,
        procedure,
        baremeRIIRoundAge,
        baremeRIIRoundTauxDFP,
        baremes,
        victime,
        dateConsolidation,
      });
    case 'PREJUDICE_ESTHETIQUE_PERMANENT':
    case 'PREJUDICE_ESTHETIQUE_TEMPORAIRE':
    case 'SOUFFRANCES_ENDUREES':
    case 'PREJUDICE_SEXUEL':
      return getInitialValuesPrejudiceMaladie({
        values: prejudiceValues as ListeProjectionMaladie | undefined,
        baremeIndemnisationId: baremeIndemnisation,
        baremeValueChoice,
        baremes,
      });
    case 'INCIDENCE_PROFESSIONNELLE':
      return getInitialValuesIncidenceProfessionnelle({
        values: prejudiceValues as
          | ListeProjectionIncidenceProfessionelle
          | undefined,
        procedure,
      });
    default:
      return undefined;
  }
};

const getListeProjectionDefaultValuesMontantTotal = (parameters: {
  prejudiceType: PrejudiceType;
  prejudiceValues: ListeProjectionExistingValues['prejudiceValues'];
  procedure: Procedure;
  PGPFReliquat: PGPFReliquatAndPGPFReliquatActivationStatus;
}) => {
  switch (parameters.prejudiceType) {
    case 'PREJUDICE_AGREMENT':
    case 'AUTRES_POSTES_DE_PREJUDICES': {
      const prejudiceValues = parameters.prejudiceValues as
        | ListeProjectionAgrement
        | undefined;
      return CalculsGlobal.sum(
        prejudiceValues?.rows?.map((row) => row.montant) || [],
      );
    }
    case 'DEFICIT_FONCTIONNEL_PERMANENT': {
      const prejudiceValues = parameters.prejudiceValues as
        | ListeProjectionFonctionnelPermanent
        | undefined;
      return CalculsFormListeProjection.getDeficitFonctionnelPermanentIndemnite(
        {
          rows: prejudiceValues?.rows || [],
        },
      );
    }
    case 'PREJUDICE_ESTHETIQUE_PERMANENT':
    case 'PREJUDICE_ESTHETIQUE_TEMPORAIRE':
    case 'SOUFFRANCES_ENDUREES':
    case 'PREJUDICE_SEXUEL': {
      const prejudiceValues = parameters.prejudiceValues as
        | ListeProjectionMaladie
        | undefined;
      return CalculsGlobal.sum(
        prejudiceValues?.rows?.map((row) => row.montant) || [],
      );
    }
    case 'INCIDENCE_PROFESSIONNELLE': {
      const prejudiceValues = parameters.prejudiceValues as
        | ListeProjectionIncidenceProfessionelle
        | undefined;
      return CalculsFormListeProjection.getIncidenceProfessionnelleIndemnite({
        partResponsabilite: parameters.procedure.partResponsabilite,
        PGPFReliquat: parameters.PGPFReliquat,
        rows: prejudiceValues?.rows || [],
        perteDeRetraite: prejudiceValues?.perteDeRetraite || {
          retraite: {
            anneeRetraite: null,
            retraiteTheoriqueTotal: 0,
            retraitePercue: 0,
            perteAnnuelle: 0,
          },
          echus: {
            montantAPayerADateCalcul: 0,
            nombreAnneesEntreAnneeRetraiteEtAnneeCalcul: 0,
          },
          aEchoir: {
            dateCapitalisation: null,
            montantCapitalise: 0,
            coefficient: null,
            sommeACapitaliser: 0,
          },
          numerosPieces: { rows: [] },
        },
      }).montantTotal;
    }
    default:
      return undefined;
  }
};

export const listeProjectionDefaultValues = (parameters: {
  values?: ListeProjectionExistingValues | undefined;
  victime?: Partial<Victime>;
  procedure: Procedure;
  baremes: Bareme[];
  prejudiceType: PrejudiceType;
  dateConsolidation: Date | undefined;
  dateLiquidation: Date | undefined;
  isExtractedValues: boolean;
  PGPFReliquat: PGPFReliquatAndPGPFReliquatActivationStatus;
}): PrejudiceFormListeProjection => {
  const {
    values,
    victime,
    procedure,
    baremes,
    prejudiceType,
    dateConsolidation,
    dateLiquidation,
    isExtractedValues,
    PGPFReliquat,
  } = parameters;
  const prorataTemporisCapitalisationBaremes = getProrataTemporisBaremes(
    baremes,
    victime?.sexe ?? 'u',
  );
  const baremeCapitalisationId =
    values && !isExtractedValues
      ? values?.baremeCapitalisation ||
        (values?.enableProrataTemporisDeces
          ? getDefaultListeProjectionBareme(
              prorataTemporisCapitalisationBaremes,
            )?._id
          : undefined)
      : procedure.baremes?.baremeCapitalisationId ?? undefined;

  const baremeCapitalisation = baremeCapitalisationId
    ? baremes.find((bareme) => bareme._id === baremeCapitalisationId)
    : undefined;

  const notes = values?.notes || '';
  const baseValues = prejudiceBaseDefaultValues({
    values: values ?? { notes: '' },
  });
  const enableProrataTemporisDeces =
    values?.enableProrataTemporisDeces || false;
  const coefficientCapitalisationAgeConsolidation =
    values?.coefficientCapitalisationAgeConsolidation ||
    (values?.enableProrataTemporisDeces
      ? !!victime?.dateNaissance &&
        dateConsolidation &&
        dateLiquidation &&
        baremeCapitalisation
        ? getCapitalisationCoefficient({
            sexe: victime.sexe ?? 'u',
            baremeValues:
              baremeCapitalisation.values as BaremeCapitalisationValue[],
            ageDateAttribution: CalculsGlobal.getAge(
              victime.dateNaissance ?? '',
              dateConsolidation,
            ),
            ageDernierArrerage: null,
            dateNaissance: victime.dateNaissance,
            dateLiquidation,
            isDernierArrerageViager: true,
          }) || null
        : null
      : undefined);
  const anneeCapitalisation = values?.anneeCapitalisation || undefined;
  const baremeIndemnisation =
    (values && !isExtractedValues
      ? values?.baremeIndemnisation
      : procedure.baremes?.baremeIndemnisationId) ?? undefined;
  const baremeValueChoice =
    (values && !isExtractedValues
      ? values?.baremeValueChoice
      : procedure.baremes?.baremeIndemnisationValueChoice) ?? 'maximum';
  const baremeRII =
    (values && !isExtractedValues
      ? values?.baremeRII
      : procedure.baremes?.baremeRIIId) ?? undefined;
  const baremeRIIRoundAge =
    (values && !isExtractedValues
      ? values?.baremeRIIRoundAge
      : procedure.baremes?.baremeRIIRoundAge) ?? undefined;
  const baremeRIIRoundTauxDFP =
    (values && !isExtractedValues
      ? values?.baremeRIIRoundTauxDFP
      : procedure.baremes?.baremeRIIRoundTauxDFP) ?? undefined;
  const prejudiceValues = getPrejudiceValues({
    prejudiceType,
    prejudiceValues: values?.prejudiceValues,
    procedure,
    baremes,
    victime,
    dateConsolidation,
    baremeRIIRoundAge,
    baremeRIIRoundTauxDFP,
    baremeIndemnisation,
    baremeValueChoice,
  });
  const montantTotal =
    values?.montantTotal ??
    getListeProjectionDefaultValuesMontantTotal({
      prejudiceType,
      prejudiceValues,
      procedure,
      PGPFReliquat,
    }) ??
    0;
  const montantProratise =
    (values && !isExtractedValues
      ? values?.montantProratise
      : procedure.baremes?.forfaitJourDFTP) ?? 0;
  return {
    ...baseValues,
    notes,
    prejudiceValues,
    montantTotal,
    enableProrataTemporisDeces,
    coefficientCapitalisationAgeConsolidation,
    anneeCapitalisation,
    baremeCapitalisation: baremeCapitalisationId,
    baremeIndemnisation,
    baremeValueChoice,
    baremeRII,
    baremeRIIRoundAge,
    baremeRIIRoundTauxDFP,
    montantProratise,
  };
};

const initialValuesRow: ListeProjectionIncidenceProfessionelleRow = {
  libelle: '',
  montant: 0,
  numerosPieces: { rows: [] },
};

const getInitialValuesIncidenceProfessionnelle = (parameters: {
  values: ListeProjectionIncidenceProfessionelle | undefined;
  procedure: Procedure;
}): ListeProjectionIncidenceProfessionelle => {
  const { values, procedure } = parameters;
  const defaultBaremeCapitalisationId =
    procedure.baremes?.baremeCapitalisationId ?? undefined;
  return {
    rows:
      (values?.rows ?? []).map((row) => ({
        ...initialValuesRow,
        ...row,
      })) || [],
    perteDeRetraite: {
      retraite: values?.perteDeRetraite?.retraite || {
        anneeRetraite: null,
        retraiteTheoriqueTotal: 0,
        retraitePercue: 0,
        perteAnnuelle: 0,
      },
      echus: values?.perteDeRetraite?.echus || {
        nombreAnneesEntreAnneeRetraiteEtAnneeCalcul: 0,
        montantAPayerADateCalcul: 0,
      },
      aEchoir: values?.perteDeRetraite?.aEchoir || {
        bareme: defaultBaremeCapitalisationId,
        sommeACapitaliser: 0,
        ageDernierArrerage: null,
        isLastArrerageViager: false,
        coefficient: null,
        montantCapitalise: 0,
        dateCapitalisation: null,
      },
      numerosPieces: values?.perteDeRetraite?.numerosPieces || { rows: [] },
    },
  };
};

const getInitialValuesPrejudiceAgrement = (
  values: ListeProjectionAgrement | undefined,
): ListeProjectionAgrement => ({
  rows:
    (values?.rows ?? []).map((row) => ({
      ...initialValuesRow,
      ...row,
    })) ?? [],
});

const getInitialValuesDeficitFonctionnelPermanent = ({
  values,
  procedure,
  baremeRIIRoundAge,
  baremeRIIRoundTauxDFP,
  baremes,
  victime,
  dateConsolidation,
}: {
  values: ListeProjectionFonctionnelPermanent | undefined;
  procedure: Procedure;
  baremeRIIRoundAge: string | undefined;
  baremeRIIRoundTauxDFP: string | undefined;
  baremes?: Bareme[];
  victime?: Partial<Victime>;
  dateConsolidation: Date | undefined;
}): ListeProjectionFonctionnelPermanent => {
  const bareme = baremes?.find(
    (bareme) =>
      bareme._id === procedure.baremes?.baremeRIIId && bareme.type === 'RII',
  );
  const getMontant = (row: Partial<ListeProjectionFonctionnelPermanentRow>) => {
    const isONIAM = bareme?.source === 'ONIAM';
    const sanitizeDirection = (
      value: string | undefined,
    ): 'down' | 'up' | null => {
      if (value === 'down' || value === 'up') return value;
      return null;
    };
    if (!isONIAM) {
      return CalculsFormListeProjection.getMontantDeficitFonctionnelPermanent({
        pourcentage: row.pourcentage || 0,
        valeurPoint: row.valeurPoint || 0,
      });
    } else if (victime && victime.dateNaissance && dateConsolidation) {
      return getONIAMRIIMontantDFP({
        bareme,
        tauxDFP: row.pourcentage || 0,
        victimeSexe: victime.sexe ?? 'u',
        victimeDateNaissance: victime.dateNaissance
          ? new Date(victime.dateNaissance)
          : new Date(),
        dateConsolidation: dateConsolidation
          ? new Date(dateConsolidation || '')
          : new Date(),
        baremeRIIRoundAge: sanitizeDirection(baremeRIIRoundAge),
        baremeRIIRoundTauxDFP: sanitizeDirection(baremeRIIRoundTauxDFP),
      });
    }
  };
  return {
    rows:
      (values?.rows ?? []).map((row) => {
        const numerosPieces = row.numerosPieces ?? { rows: [] };
        const commentaires = row.commentaires ?? '';
        const pourcentage = row.pourcentage ?? 0;
        const montant = row.montant ?? getMontant(row) ?? 0;
        const valeurPoint = row.valeurPoint ?? 0;

        return {
          numerosPieces,
          commentaires,
          pourcentage,
          montant,
          valeurPoint,
        };
      }) ?? [],
  };
};

const getInitialValuesPrejudiceMaladie = ({
  values,
  baremeIndemnisationId,
  baremeValueChoice,
  baremes,
}: {
  values: ListeProjectionMaladie | undefined;
  baremeIndemnisationId: string | undefined;
  baremeValueChoice: string | undefined;
  baremes?: Bareme[];
}): ListeProjectionMaladie => {
  return {
    rows:
      (values?.rows ?? []).map((row) => {
        const numerosPieces = row.numerosPieces ?? { rows: [] };
        const maladie = row.maladie ?? '';
        const dateDebut = row.dateDebut ?? null;
        const dateFin = row.dateFin ?? null;
        const degre = row.degre ?? undefined;
        const baremeIndemnisation = baremes?.find(
          (bareme) => bareme._id === baremeIndemnisationId,
        );
        const montantValue =
          row.montant ??
          getDefaultPrejudiceMaladieMontant(
            baremeIndemnisation as BaremeDefaultMontant,
            degre,
            baremeValueChoice,
          ) ??
          0;
        const montant = Array.isArray(montantValue)
          ? montantValue[Math.floor(montantValue.length / 2)] ?? 0
          : montantValue ?? 0;
        const jours =
          row.jours ??
          (dateDebut && dateFin
            ? CalculsGlobal.getDays(dateDebut, dateFin)
            : 0);
        return {
          numerosPieces,
          maladie,
          dateDebut,
          dateFin,
          jours,
          montant,
          degre,
        };
      }) ?? [],
  };
};
